import React, {FC, useCallback, useEffect, useState} from 'react';
import {Key} from 'ts-key-enum';

import {usePrevious} from '../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {FrontKeyboardEvent} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {usePush} from '../../../../../libs/shared/core/src/services/push/pushContext';
import {getCompanyHasTicketIdsFeature} from '../../../../../libs/shared/core/src/store/data/company/commonCompanySelectors';
import {CellsContext} from '../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {
  isSearchModifierToken,
  splitSearchQuery,
} from '../../../../../srcCommon/inboxes/search/searchQueryHelpers';
import {
  insertSuggestionInPlace,
  SuggestionSelectHandler,
} from '../../../../../srcCommon/inboxes/search/searchSuggestionsHelpers';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {InboxesSearchLocationsEnum} from '../../../../models/url/models/inboxesUrlModel';
import {
  CellsPanelSearchAppliedQuerySuggestion,
  CellsPanelSearchResultsCounts,
  HighlightAlgorithmsEnum,
} from '../../../../store/panels/cells/search/cellsPanelSearchState';
import {
  hasSearchInInboxesParameters,
  InboxesParameters,
  InboxesSearchParameters,
} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {findSearchQueryInParameters} from '../../../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {createEditorServiceChangeManager} from '../../../common/editor/change/editorServiceChangeManager';
import {createSearchTokenPlugin} from '../../../common/editor/plugins/text/searchTokenPlugin';
import {ServiceSubscription} from '../../../common/editor/serviceSubscription';
import {searchTokenClassName} from '../../../common/searchBar/searchBar';
import {useSearchBarEditor} from '../../../common/searchBar/searchBarEditor';
import {CellsHeaderFiltersProps, OnFilterSelectOption} from '../../cells/header/cellsHeaderFilters';
import {
  trackSearchClearQuery,
  trackSearchDropdownClick,
  trackSearchSessionEnd,
  trackSearchSessionStart,
} from '../searchTrackingHelpers';
import {InboxesSearchBarContent} from './inboxesSearchBarContent';

/*
 * Props.
 */

interface InboxesSearchBarProps {
  parameters: InboxesParameters;
  cellsContext?: CellsContext;
  searchFilters: CellsHeaderFiltersProps<InboxesSearchLocationsEnum>;
  queryHash?: string;
  searchResultsCounts: CellsPanelSearchResultsCounts;
  querySuggestion: CellsPanelSearchAppliedQuerySuggestion | undefined;

  isSearchQueryBuilderDropdownOpen: boolean;
  toggleSearchQueryBuilderDropdown: () => void;

  onFilterSelect: (filter: InboxesSearchLocationsEnum, options: OnFilterSelectOption) => void;
  navigateToSearch: (search: InboxesSearchParameters | undefined, wasQueryPasted?: boolean) => void;
  saveRecentQuery: (arg: string) => void;
  maybeUpdateSearchSessionTimestamp: () => void;

  setSelectedHighlightAlgorithm: (arg: HighlightAlgorithmsEnum) => void;
}

/*
 * Component.
 */

export const InboxesSearchBar: FC<InboxesSearchBarProps> = (props) => {
  const {
    parameters,
    cellsContext,
    searchFilters,
    queryHash,
    isSearchQueryBuilderDropdownOpen,
    querySuggestion,
    toggleSearchQueryBuilderDropdown,
    onFilterSelect,
    maybeUpdateSearchSessionTimestamp,
    setSelectedHighlightAlgorithm,
  } = props;

  const {isConnected} = usePush();
  const isSearchDisabled = !isConnected;

  const routeSearchQuery = findSearchQueryInParameters(parameters);

  const potentiallyCorrectedQuery = querySuggestion?.originalQuery ?? routeSearchQuery;

  // Track search sessions.
  const previousQueryHash = usePrevious(queryHash);
  useEffect(() => {
    if (queryHash !== previousQueryHash) {
      // If `previousQueryHash` is defined, it means we're ending the previous search session.
      if (previousQueryHash) {
        trackSearchSessionEnd({queryHash: previousQueryHash});
      }

      // If `queryHash` is defined, it means we're beginning a new search session.
      if (queryHash) {
        trackSearchSessionStart({queryHash});
      }
    }
  }, [previousQueryHash, queryHash]);

  const [temporarySearchFilter, setTemporarySearchFilter] = useState<InboxesSearchLocationsEnum>();
  const [pastedSearchQuery, setPastedSearchQuery] = useState(false);
  const [isQueryEdited, setIsQueryEdited] = useState(false);

  const navigateToSearch = (value: InboxesSearchParameters | undefined, wasQueryPasted?: boolean) => {
    props.navigateToSearch(value, wasQueryPasted);
    blurSearch();
  };

  /*
   * Editor.
   */

  const hasTicketIdsFeature = useAppSelector(getCompanyHasTicketIdsFeature);

  const editor = useSearchBarEditor(routeSearchQuery, () => setPastedSearchQuery(true), [
    createSearchTokenPlugin({className: searchTokenClassName, hasTicketIdsFeature}),
  ]);

  useEffect(() => {
    const changeManager = createEditorServiceChangeManager(editor.services.text);

    return changeManager.subscribe(() => {
      const newIsQueryEdited = editor.services.text.text !== potentiallyCorrectedQuery;

      return setIsQueryEdited(newIsQueryEdited);
    });
  }, [editor, parameters, potentiallyCorrectedQuery, temporarySearchFilter]);

  const getQueryText = useCallback(() => {
    const {text} = editor.services.text;
    return text.trim();
  }, [editor.services.text]);

  const setQueryText = useCallback(
    (queryText: string) => {
      editor.services.text.setText(queryText);
      // Workaround for https://frontjira.atlassian.net/browse/PB-15377
      editor.services.selection.moveToEnd();
    },
    [editor.services.selection, editor.services.text],
  );

  const clearQueryText = () => {
    trackSearchClearQuery({queryHash});

    setQueryText('');
    editor.services.focus.blur();
  };

  const blurSearch = () => {
    editor.services.focus.blur();
  };

  /*
   * Lifecycle.
   */

  // If querySuggestion is cleared (user clicked on P2 banner's CTA), or
  // a query suggestion just got applied, update the search bar content.
  // Same if we navigated.

  useEffect(() => {
    setQueryText(potentiallyCorrectedQuery);
  }, [setQueryText, potentiallyCorrectedQuery]);

  /*
   * Event handlers.
   */

  const onSearchKeyDown: (event: FrontKeyboardEvent) => void = (event) => {
    if (event.key === Key.Escape) {
      blurSearch();
      return;
    }

    if (event.key !== Key.Enter) {
      // Set pastedSearchQuery to false as user is typing.
      if (pastedSearchQuery) {
        setPastedSearchQuery(false);
      }

      // Return early as we're only interested in "Enter" events.
      return;
    }

    onSearchEnter(event);
  };

  const onSearchEnter = (
    event: FrontKeyboardEvent | undefined,
    queryToSave?: string,
    locationOverride?: InboxesSearchLocationsEnum,
  ) => {
    if (event) {
      event.preventDefault();
    }

    setIsQueryEdited(false);

    // Clear the temporary filter value.
    setTemporarySearchFilter(undefined);

    // If the search query is empty, clear the results.
    const queryText = getQueryText();
    if (!queryText || queryText.length === 0) {
      navigateToSearch(undefined);
      return;
    }

    // Save recent query.
    props.saveRecentQuery(queryToSave ?? queryText);

    const searchQueryParts = splitSearchQuery(queryText, hasTicketIdsFeature);
    const query = searchQueryParts
      .map(({text, token}) => {
        // Using token.value allows to remove useless spaces.
        if (token && isSearchModifierToken(token, hasTicketIdsFeature)) {
          return `${token.modifier}:${token.value}`;
        }

        return text;
      })
      .join('');

    // Otherwise, navigate to the results.
    const searchValue = {
      location: locationOverride ?? temporarySearchFilter ?? searchFilters.selectedFilter,
      query,
    };
    navigateToSearch(searchValue, pastedSearchQuery);
  };

  const onExitClick: () => void = () => {
    clearQueryText();

    if (!hasSearchInInboxesParameters(props.parameters)) {
      return;
    }

    navigateToSearch(undefined);
  };

  /*
   * Query builder dropdown.
   */

  const onQueryBuilderSearch = (newQueryText: string, location: InboxesSearchLocationsEnum) => {
    // Set new query.
    setQueryText(newQueryText);

    // Navigate to search.
    // FIXME(ldemarne): We can't use setTemporarySearchFilter because the change will only be picked up on the next render.
    onSearchEnter(undefined, undefined, location);
  };

  /*
   * Highlight algorithm dropdown.
   */

  const onHighlightAlgorithmSelect = (algorithm: HighlightAlgorithmsEnum) => {
    setSelectedHighlightAlgorithm(algorithm);

    // Search again if the user was already searching.
    if (hasSearchInInboxesParameters(props.parameters)) {
      navigateToSearch(props.parameters.search);
    }
  };

  /*
   * Suggestions dropdown.
   */

  const onSuggestionSelect: SuggestionSelectHandler = (text, selectKey, options = {}) => {
    const {
      insertWhiteSpace,
      query = null, // @TODO: Remove this, we could use `searchQuery`.
      replaceWholeQuery = false,
      shouldSaveOriginalQuery = false,
      isRecentQuery = false,
      isSmartMixItem = false,
      isModifierSuggestion = false,
      modifierSuggestionType = null,
      isConversationSuggestion = false,
      topResultsConversationId = null,
      conversationClassificationLabels,
      modifierHelperType = null,
      smartMixType = null,
      smartMixValue = null,
      smartMixIndex = null,
      smartMixNamespace = null,
      smartMixScore = null,
      smartMixResourceId = null,
      isClientSideFiltering = null,
    } = options;

    /**
     * Insert the text in the search query in place of:
     *   - the entire query if `replaceWholeQuery` is true,
     *   - the last token otherwise.
     */
    const {textUnderCursor} = editor.services.textUnderCursor;
    const queryText = textUnderCursor.text;

    // eslint-disable-next-line no-nested-ternary
    const newQueryText = replaceWholeQuery
      ? `${text}${insertWhiteSpace ? ' ' : ''}`
      : insertSuggestionInPlace(queryText, text, textUnderCursor.focusOffset, insertWhiteSpace);

    editor.services.focus.focus();
    editor.services.textUnderCursor.setTextUnderCursor({
      text: newQueryText,
      focusOffset: newQueryText.length,
      anchorOffset: newQueryText.length,
    });

    // We can safely navigate if:
    // * the newly inserted text is not of the shape `modifier:` AND
    // * the suggestion was not selected with a tab.
    const selectedWithTab = selectKey === Key.Tab;
    const queryParts = splitSearchQuery(newQueryText);
    const lastPart = queryParts[queryParts.length - 1];

    const lastPartIsEmptyModifier =
      lastPart.token && isSearchModifierToken(lastPart.token) && lastPart.token.value === '';

    if (!lastPartIsEmptyModifier && !selectedWithTab) {
      onSearchEnter(undefined, shouldSaveOriginalQuery ? queryText : undefined);
    }

    // Report data event.
    trackSearchDropdownClick({
      queryHash,
      isRecentQuery,
      isSmartMixItem,
      isModifierSuggestion,
      modifierSuggestionType,
      isConversationSuggestion,
      topResultsConversationId,
      topResultsQuery: isConversationSuggestion ? query : null,
      conversationClassificationLabels,
      modifierHelperType,
      selectedWithTab,
      smartMixType,
      smartMixValue,
      smartMixIndex,
      smartMixQuery: isSmartMixItem ? query : null,
      smartMixNamespace,
      smartMixScore,
      smartMixResourceId,
      isClientSideFiltering,
      inputQuery: routeSearchQuery,
      outputQuery: newQueryText.trim(),
    });
  };

  /*
   * Render.
   */

  return (
    <ServiceSubscription source={editor.services.focus}>
      {({isActive}) => (
        <InboxesSearchBarContent
          editor={editor}
          editorHasFocus={isActive}
          isDisabled={isSearchDisabled}
          isInSearch={routeSearchQuery.length > 0}
          isComposing={isQueryEdited}
          onKeyDown={onSearchKeyDown}
          onExitClick={onExitClick}
          searchQuery={routeSearchQuery}
          searchFilters={searchFilters}
          temporarySearchFilter={temporarySearchFilter}
          selectedFilter={temporarySearchFilter || searchFilters.selectedFilter}
          onFilterSelect={onFilterSelect}
          onLocalFilterSelect={setTemporarySearchFilter}
          namespace={cellsContext?.namespace}
          parameters={parameters}
          onSuggestionSelect={onSuggestionSelect}
          isQueryBuilderDropdownOpen={isSearchQueryBuilderDropdownOpen}
          toggleIsQueryBuilderDropdownOpen={toggleSearchQueryBuilderDropdown}
          onQueryBuilderSearch={onQueryBuilderSearch}
          maybeUpdateSearchSessionTimestamp={maybeUpdateSearchSessionTimestamp}
          queryHash={queryHash}
          onHighlightAlgorithmSelect={onHighlightAlgorithmSelect}
        />
      )}
    </ServiceSubscription>
  );
};
